import { useState, useEffect, useRef, useCallback } from 'react';
import { WebRTCSignaling } from './WebRTCSignaling';

const ICE_SERVERS = {
    iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
        { urls: 'stun:stun3.l.google.com:19302' },
        { urls: 'stun:stun4.l.google.com:19302' }
    ]
};

export const useWebRTC = ({ roomId, userId, role = 'student', global = {} }) => {
    const [localStream, setLocalStream] = useState(null);
    const [remoteStream, setRemoteStream] = useState(null);
    const [screenStream, setScreenStream] = useState(null);
    const [connectionState, setConnectionState] = useState('idle'); // idle | acquiring | waiting_peer | connecting | connected | disconnected | failed
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(false);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [dataMessages, setDataMessages] = useState([]);
    const [remotePeerInfo, setRemotePeerInfo] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    const peerConnection = useRef(null);
    const signaling = useRef(null);
    const dataChannel = useRef(null);
    const localStreamRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);

    // 1. Initialize Local Media (Camera + Mic)
    const startLocalMedia = useCallback(async (constraints = { video: true, audio: true }) => {
        try {
            setConnectionState('acquiring');
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            localStreamRef.current = stream;
            setLocalStream(stream);
            setupAudioAnalyser(stream);
            setConnectionState('waiting_peer');
            return stream;
        } catch (error) {
            console.error('[useWebRTC] Error acquiring media devices:', error);
            setConnectionState('error');
            throw error;
        }
    }, []);

    // 2. Setup Audio Activity Monitor
    const setupAudioAnalyser = (stream) => {
        try {
            const audioTrack = stream.getAudioTracks()[0];
            if (!audioTrack) return;

            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return;

            const audioCtx = new AudioCtx();
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 256;
            const source = audioCtx.createMediaStreamSource(new MediaStream([audioTrack]));
            source.connect(analyser);

            audioContextRef.current = audioCtx;
            analyserRef.current = analyser;

            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            const checkVolume = () => {
                if (!analyserRef.current) return;
                analyserRef.current.getByteFrequencyData(dataArray);
                let sum = 0;
                for (let i = 0; i < dataArray.length; i++) {
                    sum += dataArray[i];
                }
                const avg = sum / dataArray.length;
                setIsSpeaking(avg > 15);
                requestAnimationFrame(checkVolume);
            };
            checkVolume();
        } catch (e) {
            console.warn('[useWebRTC] Audio analyser setup failed:', e);
        }
    };

    // 3. Create or Reset PeerConnection
    const createPeerConnection = useCallback(() => {
        if (peerConnection.current) {
            peerConnection.current.close();
        }

        const pc = new RTCPeerConnection(ICE_SERVERS);

        pc.onicecandidate = (event) => {
            if (event.candidate && signaling.current) {
                signaling.current.send('ICE_CANDIDATE', event.candidate);
            }
        };

        pc.ontrack = (event) => {
            if (event.streams && event.streams[0]) {
                setRemoteStream(event.streams[0]);
                setConnectionState('connected');
            }
        };

        pc.onconnectionstatechange = () => {
            const state = pc.connectionState;
            if (state === 'connected') {
                setConnectionState('connected');
            } else if (state === 'disconnected' || state === 'failed') {
                setConnectionState('disconnected');
            } else if (state === 'connecting') {
                setConnectionState('connecting');
            }
        };

        // Add Local Tracks to PC
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((track) => {
                pc.addTrack(track, localStreamRef.current);
            });
        }

        // Setup DataChannel
        if (role === 'student') {
            const dc = pc.createDataChannel('bi_chat_notes', { ordered: true });
            setupDataChannel(dc);
        } else {
            pc.ondatachannel = (event) => {
                setupDataChannel(event.channel);
            };
        }

        peerConnection.current = pc;
        return pc;
    }, [role]);

    const setupDataChannel = (dc) => {
        dataChannel.current = dc;
        dc.onopen = () => console.log('[useWebRTC] DataChannel open');
        dc.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                setDataMessages((prev) => [...prev, data]);
            } catch (e) {
                setDataMessages((prev) => [...prev, { text: event.data, sender: 'Peer', timestamp: Date.now() }]);
            }
        };
    };

    // 4. Initiate Offer (Caller)
    const initiateCall = useCallback(async () => {
        try {
            setConnectionState('connecting');
            const pc = createPeerConnection();
            const offer = await pc.createOffer({
                offerToReceiveAudio: true,
                offerToReceiveVideo: true
            });
            await pc.setLocalDescription(offer);

            if (signaling.current) {
                signaling.current.send('OFFER', offer);
            }
        } catch (error) {
            console.error('[useWebRTC] Error initiating call:', error);
            setConnectionState('failed');
        }
    }, [createPeerConnection]);

    // 5. Handle Received Offer (Callee)
    const handleOffer = useCallback(async (offer, senderInfo) => {
        try {
            setRemotePeerInfo(senderInfo);
            setConnectionState('connecting');
            const pc = createPeerConnection();
            await pc.setRemoteDescription(new RTCSessionDescription(offer));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);

            if (signaling.current) {
                signaling.current.send('ANSWER', answer);
            }
        } catch (error) {
            console.error('[useWebRTC] Error handling offer:', error);
        }
    }, [createPeerConnection]);

    // 6. Handle Received Answer
    const handleAnswer = useCallback(async (answer, senderInfo) => {
        try {
            setRemotePeerInfo(senderInfo);
            if (peerConnection.current && peerConnection.current.signalingState !== 'stable') {
                await peerConnection.current.setRemoteDescription(new RTCSessionDescription(answer));
            }
        } catch (error) {
            console.error('[useWebRTC] Error handling answer:', error);
        }
    }, []);

    // 7. Handle Received ICE Candidate
    const handleIceCandidate = useCallback(async (candidate) => {
        try {
            if (peerConnection.current && candidate) {
                await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
            }
        } catch (error) {
            console.error('[useWebRTC] Error adding ICE candidate:', error);
        }
    }, []);

    // 8. Connect Signaling & Event Hooks
    useEffect(() => {
        if (!roomId || !userId) return;

        const sig = new WebRTCSignaling(roomId, userId, role, {
            apiDomain: global.domain,
            apiCode: global.apicode
        });

        sig.on('onPeerJoined', (peer) => {
            setRemotePeerInfo(peer);
            if (role === 'student') {
                // Student automatically sends offer when peer joins
                initiateCall();
            }
        });

        sig.on('onOffer', (offer, msg) => handleOffer(offer, msg));
        sig.on('onAnswer', (answer, msg) => handleAnswer(answer, msg));
        sig.on('onIceCandidate', (cand) => handleIceCandidate(cand));
        sig.on('onPeerLeft', () => {
            setRemoteStream(null);
            setRemotePeerInfo(null);
            setConnectionState('waiting_peer');
        });
        sig.on('onMessage', (msg) => {
            if (msg.type === 'CHAT') {
                setDataMessages((prev) => [...prev, msg.payload]);
            }
        });

        sig.start();
        signaling.current = sig;

        return () => {
            sig.destroy();
        };
    }, [roomId, userId, role, global, initiateCall, handleOffer, handleAnswer, handleIceCandidate]);

    // 9. Media Control Utilities (Mute, Toggle Cam, Screen Share)
    const toggleMute = () => {
        if (localStreamRef.current) {
            const audioTrack = localStreamRef.current.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                setIsMuted(!audioTrack.enabled);
            }
        }
    };

    const toggleVideo = () => {
        if (localStreamRef.current) {
            const videoTrack = localStreamRef.current.getVideoTracks()[0];
            if (videoTrack) {
                videoTrack.enabled = !videoTrack.enabled;
                setIsVideoOff(!videoTrack.enabled);
            }
        }
    };

    const startScreenShare = async () => {
        try {
            if (isScreenSharing) {
                stopScreenShare();
                return;
            }
            const screen = await navigator.mediaDevices.getDisplayMedia({ video: true });
            setScreenStream(screen);
            setIsScreenSharing(true);

            const screenTrack = screen.getVideoTracks()[0];
            if (peerConnection.current) {
                const sender = peerConnection.current.getSenders().find((s) => s.track && s.track.kind === 'video');
                if (sender) {
                    sender.replaceTrack(screenTrack);
                }
            }

            screenTrack.onended = () => {
                stopScreenShare();
            };
        } catch (err) {
            console.warn('[useWebRTC] Screen share cancelled/failed:', err);
        }
    };

    const stopScreenShare = () => {
        if (screenStream) {
            screenStream.getTracks().forEach((t) => t.stop());
            setScreenStream(null);
        }
        setIsScreenSharing(false);

        if (localStreamRef.current && peerConnection.current) {
            const cameraTrack = localStreamRef.current.getVideoTracks()[0];
            const sender = peerConnection.current.getSenders().find((s) => s.track && s.track.kind === 'video');
            if (sender && cameraTrack) {
                sender.replaceTrack(cameraTrack);
            }
        }
    };

    const sendMessage = (text, senderName = 'Alumno') => {
        const msg = {
            id: Date.now(),
            text,
            sender: senderName,
            role,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        // Send via data channel
        if (dataChannel.current && dataChannel.current.readyState === 'open') {
            try {
                dataChannel.current.send(JSON.stringify(msg));
            } catch (e) {}
        }

        // Send via signaling broadcast
        if (signaling.current) {
            signaling.current.send('CHAT', msg);
        }

        setDataMessages((prev) => [...prev, msg]);
    };

    const endCall = () => {
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((t) => t.stop());
            localStreamRef.current = null;
        }
        if (screenStream) {
            screenStream.getTracks().forEach((t) => t.stop());
        }
        if (peerConnection.current) {
            peerConnection.current.close();
            peerConnection.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        setLocalStream(null);
        setRemoteStream(null);
        setScreenStream(null);
        setConnectionState('idle');
    };

    return {
        localStream,
        remoteStream,
        screenStream,
        connectionState,
        isMuted,
        isVideoOff,
        isScreenSharing,
        isSpeaking,
        remotePeerInfo,
        dataMessages,
        startLocalMedia,
        toggleMute,
        toggleVideo,
        startScreenShare,
        stopScreenShare,
        sendMessage,
        initiateCall,
        endCall
    };
};
